import { Navigate, Outlet } from 'react-router-dom';
import { hasSessionToken } from '@shared/api/session';
import { PATH_AUTH } from '../paths';

/**
 * Route-guard авторизованных зон (OSI-806).
 *
 * ЗАЧЕМ:
 * Неавторизованный пользователь, открывающий сохранённую вкладку `/osi` (восстановление вкладок
 * браузером), видел пустую страницу. Route-guard'ов в проекте не было вообще, а единственный
 * механизм редиректа — `AuthServiceImpl.sessionExpired()` — срабатывает ТОЛЬКО из 401-веток
 * axios-интерцепторов. На `/osi` без токена ни одного защищённого запроса не уходит
 * (osiId пуст, справочники загейчены по `authModule.userData?.id` — OSI-796), а единственный
 * уходящий POST `/api/KeyValues` помечен allow_anonymous и отвечает 200. Значит 401 нет —
 * значит и редиректа нет. Гвард закрывает эту дыру на уровне роутера, до рендера страницы.
 *
 * ПОЧЕМУ ИСТОЧНИК ПРАВДЫ — `hasSessionToken()`, А НЕ MobX-СТОР:
 * 1. MobX-сторы (`authModule.id` / `authModule.userData`) гидратируются из localStorage
 *    АСИНХРОННО через `mobx-persist-store`. На первом рендере роутера они ещё пусты, поэтому
 *    проверка по стору выкинула бы на авторизацию даже реально залогиненного пользователя —
 *    это гонка гидратации.
 * 2. Прямая проверка вида `localStorage.getItem('token') !== null` тоже не годится:
 *    `logout()` и `sessionExpired()` кладут в ключ ПУСТУЮ СТРОКУ, а не удаляют его
 *    (см. `app/stores/mobx/services/auth/index.ts`), т.е. после выхода ключ существует и
 *    такая проверка дала бы ложноположительный результат.
 * `hasSessionToken()` (`shared/api/session.ts`) трактует и `null`, и `''` как «сессии нет» —
 * ровно та же семантика, что уже используют axios-интерцепторы (OSI-796). Один источник правды.
 *
 * КУДА РЕДИРЕКТИМ:
 * На `PATH_AUTH.checkContact`, а не на `/auth/login`: страница логина без `location.state.phone`
 * сама выкидывает пользователя обратно (`pages/auth/Login/index.tsx`), т.е. редирект на неё
 * дал бы «мигание» и повторный отскок. `replace` обязателен — иначе кнопка «Назад» вернула бы
 * пользователя на защищённую страницу, и он снова увидел бы пустой каркас.
 *
 * ВОЗВРАТА НА ИСХОДНЫЙ URL НЕТ СОЗНАТЕЛЬНО: returnUrl/redirect-параметры не заводим,
 * штатный флоу «вход → выбор роли → /cabinet» остаётся без изменений (согласовано по OSI-806).
 */
export const AuthGuard = () => {
  // Токена нет (ключа не было или там пустая строка после logout/sessionExpired) — на авторизацию.
  if (!hasSessionToken()) return <Navigate to={PATH_AUTH.checkContact} replace />;

  // Токен есть — пропускаем. Вложенные layout'ы и страницы рендерятся через <Outlet/>,
  // поэтому гвард НЕ ломает существующую вложенность роутов.
  return <Outlet />;
};

export default AuthGuard;
